import React, { useState, useCallback, useMemo, useEffect } from 'react';
import '../css/Promociones.css';
import { ReactComponent as Eliminar } from '../assets/icon/eliminar.svg';
import { ReactComponent as Editar } from '../assets/icon/editar.svg';
import { ReactComponent as Agregar } from '../assets/icon/agregarEvento.svg';
import { ReactComponent as Recargar } from '../assets/icon/recargar.svg';
import { useNavigate } from "react-router-dom";
import TablaPromociones from '../Promociones/TablaPromociones';
import { usePromociones, deletePromocion } from '../services/promociones';
import { useToast } from '../contexts/ToastContext';
import BulkDeleteModal from '../Components/BulkDeleteModal';

function Promociones() {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingItems, setDeletingItems] = useState([]);
  const { showError, showSuccess } = useToast();

  const { data: promociones, loading, error, refetch } = usePromociones({
    immediate: true
  });

  useEffect(() => {
    if (error) {
      showError(error, 'Error de conexión');
    }
  }, [error, showError]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    
    const itemsToDelete = promociones.filter(p => selectedIds.includes(p.IdPromocion));
    setDeletingItems(itemsToDelete);
    setShowDeleteModal(true);
  }, [selectedIds, promociones]);

  const handleConfirmDelete = useCallback(async (id, item) => {
    const result = await deletePromocion(id);
    if (result.success && result.error === 0) {
      return { success: true };
    }
    throw new Error(result.mensaje || 'Error al eliminar');
  }, []);

  const handleDeleteComplete = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedIds([]);
    refetch();
    showSuccess('Promoción(es) eliminada(s) correctamente');
  }, [refetch, showSuccess]);

  const handleEditSelected = useCallback(() => {
    if (selectedIds.length === 1) {
      navigate(`/dashboard/promociones/editarpromociones/${selectedIds[0]}`);
    }
  }, [selectedIds, navigate]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const buttonStates = useMemo(() => {
    const hasNoSelection = selectedIds.length === 0;
    const hasSingleSelection = selectedIds.length === 1;
    const hasMultipleSelection = selectedIds.length > 1;

    return {
      nuevo: hasNoSelection,
      editar: hasSingleSelection,
      eliminar: hasSingleSelection || hasMultipleSelection
    };
  }, [selectedIds]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="bodyPromociones">

      {/* TÍTULO */}
      <div className='tituloPromociones'><p>Promociones</p></div>

      {/* CONTENIDO SUPERIOR */}
      <div className='contenidoSuperiorPromociones'>
        <div className='botonesPromociones'>
          <button
            className='botonesEdicionPromociones'
            onClick={() => navigate("/dashboard/promociones/nuevapromocion")}
            disabled={!buttonStates.nuevo}
            title={buttonStates.nuevo ? "Agregar nueva promoción" : "Deshabilitado cuando hay selección"}
          >
            <div className="svgBotonesPromociones"><Agregar className="logo-svgBotones" /></div>
            Agregar Promocion
          </button>

          <button
            className='botonesEdicionEmpresas'
            disabled={!buttonStates.editar}
            onClick={handleEditSelected}
            title={buttonStates.editar ? "Editar promoción seleccionada" : "Selecciona una promoción para editar"}
          >
            <div className="svgBotonesPromociones"><Editar className="logo-svgBotones" /></div>
            Editar Promocion
          </button>

          <button 
            className='botonesEdicionPromociones'
            disabled={!buttonStates.eliminar}
            onClick={handleDeleteSelected}
            title={buttonStates.eliminar ? `Eliminar ${selectedIds.length} promoción(es)` : "Selecciona promociones para eliminar"}
          >
            <div className="svgBotonesEmpresas"><Eliminar className="logo-svgBotones" /></div>
            Eliminar Promocion{selectedIds.length > 1 ? 'es' : ''}
          </button>

          <button 
            className='botonesEdicionPromociones'
            onClick={handleRefresh}
            disabled={loading}
            title="Recargar promociones"
          >
            <div className="svgBotonesPromociones">
              <Recargar className={`logo-svgBotones ${loading ? 'rotating' : ''}`} />
            </div>
            Recargar
          </button>
        </div>
      </div>

      {/* AQUÍ SE MOSTRARÁ LA TABLA */}
      <div className="tablaContainerPromociones">
        <TablaPromociones 
          promociones={promociones}
          loading={loading}
          error={error}
          onRetry={refetch}
          emptyStateNoReload={true}
          pageSize={20}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSelectionChange={(selectedIdsList) => {
            setSelectedIds(selectedIdsList || []);
            if (currentPage !== 1) {
              setCurrentPage(1);
            }
          }}
        />
      </div>

      <BulkDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Eliminar Promociones"
        items={deletingItems}
        onDelete={handleConfirmDelete}
        getItemId={(item) => item.IdPromocion}
        getItemLabel={(item) => item.Titulo || `Promoción #${item.IdPromocion}`}
        entityName="promoción"
      />

    </div>
  );
}

export default Promociones;
